import React, { useState, useEffect } from 'react';
import { Input, Table, Icon, List, Descriptions, Checkbox } from 'antd';
import _ from 'lodash';
import './Table.scss';

export default ({
  title = 'data',
  columns = [],
  dataSource = [],
  breakpoint = 768,
  searchFields,
  showExpiredToggle,
  searchPlaceholder,
  moduleEmpty = null,
  ...props
}) => {
  const [mode, setMode] = useState('table');
  const [filterText, setFilterText] = useState('');
  const [showExpired, setShowExpired] = useState(false);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth <= breakpoint) {
        setMode('list');
      } else {
        setMode('table');
      }
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [breakpoint]);

  const onFilterTextChange = e => {
    const query = e.target.value.replace(/\W|_/g, '[$&]');
    setFilterText(query);
  };

  const filter = (data, filterQuery) => {
    let newData = data;
    if (!showExpired) {
      newData = data.filter(item => !item.isExpired);
    }
    if (filterQuery === '') {
      return newData;
    }
    const regex = new RegExp(filterQuery, 'i');
    return newData.filter(o =>
      _.reduce(
        searchFields,
        (result, value) => {
          const fieldValue = o[value];
          if (fieldValue == null || fieldValue === '') return result;
          return result || !!String(fieldValue).match(regex);
        },
        false
      )
    );
  };

  const onToggleChange = e => {
    setShowExpired(e.target.checked);
  };

  const renderDescriptionItem = (item, column) => {
    const { dataIndex, render } = column;
    return render ? render(item[dataIndex], item) : item[dataIndex];
  };

  const filteredData = filter(dataSource || [], filterText);
  const isTrulyEmpty = !dataSource || dataSource.length === 0;
  const showModuleEmptyPlaceholder = Boolean(moduleEmpty) && isTrulyEmpty;
  const tableColumns = columns.map(column => {
    if (column.key !== 'action') {
      return column;
    }

    const renderAction = column.render;

    return {
      ...column,
      className: ['table-action-column', column.className].filter(Boolean).join(' '),
      render: (text, record, index) => (
        <span className="table-action-items">
          {renderAction ? renderAction(text, record, index) : text}
        </span>
      )
    };
  });

  const filteredButNoRows = filteredData.length === 0 && !isTrulyEmpty;
  const emptyBodyText = filteredButNoRows ? 'No results match your search.' : `No ${title}`;

  if (showModuleEmptyPlaceholder) {
    return <div className="table-container table-container--module-empty">{moduleEmpty}</div>;
  }

  return (
    <div className="table-container">
      {searchFields && searchFields.length > 0 && (
        <div className="table-search-container">
          {showExpiredToggle && (
            <Checkbox onChange={onToggleChange} className="table-toggle">
              Show Expired
            </Checkbox>
          )}
          <Input
            placeholder={searchPlaceholder || `Search ${title}...`}
            prefix={<Icon type="search" />}
            onChange={onFilterTextChange}
            className="table-filter"
          />
        </div>
      )}

      {mode === 'table' ? (
        <Table
          columns={tableColumns}
          dataSource={filteredData}
          rowClassName={record => (record.isExpired ? 'isExpired' : '')}
          pagination={{ hideOnSinglePage: true }}
          locale={{ emptyText: emptyBodyText }}
          scroll={{ x: tableColumns.length * 120 }}
          {...props}
        />
      ) : (
        <List
          grid
          split={false}
          dataSource={filteredData}
          itemLayout="vertical"
          pagination={{ hideOnSinglePage: true }}
          renderItem={item => (
            <List.Item>
              <Descriptions bordered size="small" column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 2, xs: 1 }}>
                {tableColumns.map(
                  column =>
                    (column.title || column.key === 'action') && (
                      <Descriptions.Item
                        key={column.key}
                        label={column.title}
                        span={column.span}
                        className={column.className}
                      >
                        {renderDescriptionItem(item, column)}
                      </Descriptions.Item>
                    )
                )}
              </Descriptions>
            </List.Item>
          )}
        />
      )}
    </div>
  );
};
